import {
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  FilterFilled,
  LoadingOutlined,
  MailOutlined,
  PhoneOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, Col, Flex, List, Row, Spin, Tag, Typography } from "antd";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ViewType } from "../../../components/CreateNoteModal/AITemplateModal/types";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux-hooks";
import { useIsMobileView } from "../../../hooks/ui-hook";
import {
  type CallSkill,
  useArchiveCallMutation,
  useGetArchivedCallDashboardDataQuery,
  useGetCallDashboardDataQuery,
  useGetCallSkillsQuery,
} from "../../../service/calls.service";
import { setSelectedView } from "../../../slices/appStateSlice";
import CallDashboardLandingPage from "./CallDashboardLandingPage.component";
import TranscriptModal from "./TranscriptModal";
import styles from "./call-dashboard.module.scss";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

// Helper function to format timestamp with timezone
const userTimezone = dayjs.tz.guess();

const formatCallDateTime = (isoTimestamp: string): string => {
  return dayjs.utc(isoTimestamp).tz(userTimezone).format("MMM D, YYYY [at] h:mm A");
};

// Default icon for skills
const skillIcon = (
  <svg
    style={{ marginRight: "5px" }}
    width="14"
    height="11"
    viewBox="0 0 14 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.99967 5.5C8.38038 5.5 9.49967 4.38071 9.49967 3C9.49967 1.61929 8.38038 0.5 6.99967 0.5C5.61896 0.5 4.49967 1.61929 4.49967 3C4.49967 4.38071 5.61896 5.5 6.99967 5.5Z"
      fill="#6B7280"
    />
    <path
      d="M13.9997 10.5H0.999674C0.999674 8.01472 3.01439 6 5.49967 6H8.49967C10.985 6 12.9997 8.01472 12.9997 10.5H13.9997Z"
      fill="#6B7280"
    />
  </svg>
);

const dayFilters = [
  { key: "All Time", label: "All Time" },
  { key: "Today", label: "Today" },
  { key: "Yesterday", label: "Yesterday" },
  { key: "Last 7 Days", label: "Last 7 Days" },
];

const CallDashboard = () => {
  const [pageSize] = useState<number>(50);
  const [selectedTag, setSelectedTag] = useState<string>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [transcriptCallName, setTranscriptCallName] = useState<string>("");
  const [transcriptCallId, setTranscriptCallId] = useState<number | null>(null);
  const [selectedDayFilter, setSelectedDayFilter] = useState<string>("All Time");

  const navigate = useNavigate();
  const isMobile = useIsMobileView();
  const { userInfo } = useAppSelector((state) => state.auth);

  // Fetch call dashboard data from API
  const { data, error, isLoading, refetch } = useGetCallDashboardDataQuery({
    page: currentPage,
  });
  const {
    data: archivedData,
    error: archivedError,
    isLoading: archivedLoading,
    refetch: refetchArchived,
  } = useGetArchivedCallDashboardDataQuery({
    page: currentPage,
  });
  const { data: skillsData } = useGetCallSkillsQuery();
  const [archiveCall] = useArchiveCallMutation();
  const dispatch = useAppDispatch();

  // Use the appropriate data based on showArchived state
  const currentData = showArchived ? archivedData : data;
  const currentError = showArchived ? archivedError : error;
  const currentLoading = showArchived ? archivedLoading : isLoading;
  const currentRefetch = showArchived ? refetchArchived : refetch;

  useEffect(() => {
    if (isMobile) {
      navigate("/home");
    }
    dispatch(setSelectedView(ViewType.AI_RECEPTIONIST));
  }, []);

  // Reset current page when selected tag, day filter, or showArchived changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTag, selectedDayFilter, showArchived]);

  // Helper function to filter calls by date
  const filterCallsByDate = (calls: any[]) => {
    if (!calls || selectedDayFilter === "All Time") {
      return calls;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const last7Days = new Date(today);
    last7Days.setDate(last7Days.getDate() - 7);

    return calls.filter((call) => {
      // Parse the ISO timestamp
      const callDate = new Date(call.date);

      switch (selectedDayFilter) {
        case "Today":
          return callDate >= today;
        case "Yesterday":
          return callDate >= yesterday && callDate < today;
        case "Last 7 Days":
          return callDate >= last7Days;
        default:
          return true;
      }
    });
  };

  const handleArchiveCall = async (callId: number) => {
    try {
      await archiveCall(callId).unwrap();
      // The mutation will automatically invalidate and refetch the data
    } catch (error) {
      console.error("Failed to archive call:", error);
    }
  };

  const handleViewTranscript = (callId: number, callName: string) => {
    setTranscriptCallId(callId);
    setTranscriptCallName(callName);
  };

  const handleCloseTranscript = () => {
    setTranscriptCallId(null);
    setTranscriptCallName("");
  };

  // Get urgent calls count (only this needs frontend calculation for filtering)
  // Note: This now only counts urgent calls on the current page, not across all pages
  const urgentCalls = useMemo(() => {
    if (!currentData?.calls) {
      return [];
    }
    return currentData.calls.filter((call) => call.skills?.some((skill) => skill.code === "urgent"));
  }, [currentData?.calls]);

  const stats = currentData?.stats || {
    total_calls: 0,
    new_patients: 0,
    existing_patients: 0,
  };

  const renderPatientList = (patients: any[]) => (
    <List
      className={styles.dashboard__tabs_list}
      dataSource={patients}
      pagination={{
        align: "center",
        current: currentPage,
        pageSize: pageSize,
        total: currentData?.pagination?.count || 0,
        showSizeChanger: false,
        showQuickJumper: true,
        onChange: (page) => {
          setCurrentPage(page);
        },
      }}
      renderItem={(patient) => (
        <List.Item>
          <div className={styles.dashboard__tabs_patient_info}>
            <div className={styles.dashboard__tabs_patient_header}>
              <div className={styles.dashboard__tabs_patient_name_container}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Title level={5} className={styles.dashboard__tabs_patient_name} style={{ margin: 0 }}>
                    {patient.name}
                  </Title>
                  {/* Display skills after phone with middot separator */}
                  {patient.skills && patient.skills.length > 0 && (
                    <>
                      {<span style={{ color: "#6B7280", fontSize: "16px" }}>•</span>}
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        {(() => {
                          // Sort skills to put urgent first, then others
                          const sortedSkills = [...patient.skills].sort((a, b) => {
                            if (a.code === "urgent") return -1;
                            if (b.code === "urgent") return 1;
                            return 0;
                          });

                          return sortedSkills.map((skill: CallSkill) => (
                            <Tag
                              key={skill.id}
                              color={skill.code === "urgent" ? "red" : "blue"}
                              style={{
                                margin: 0,
                                fontSize: "12px",
                                backgroundColor: skill.code === "urgent" ? "#FEE2E2" : undefined,
                                borderColor: skill.code === "urgent" ? "#FEE2E2" : "transparent",
                                color: skill.code === "urgent" ? "#DC2626" : undefined,
                              }}
                            >
                              {skill.label}
                            </Tag>
                          ));
                        })()}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <Flex gap={10}>
                <CalendarOutlined className={styles.dashboard__tabs_patient_contact_item_icon} />
                <Text className={styles.dashboard__tabs_patient_date}>{formatCallDateTime(patient.date)}</Text>
              </Flex>
            </div>
            <Flex gap={10}>
              {/* Display phone number after name */}
              {(patient.from_number || patient.phone) && (
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <PhoneOutlined style={{ color: "#6B7280", fontSize: "14px" }} />
                  <a
                    href={`tel:${patient.from_number || patient.phone}`}
                    style={{ color: "#1890ff", textDecoration: "none", fontSize: "14px" }}
                    title={`Call ${patient.from_number || patient.phone}`}
                  >
                    {patient.from_number || patient.phone}
                  </a>
                </div>
              )}
              {patient.email && (
                <div className={styles.dashboard__tabs_patient_contact_item}>
                  {(patient.from_number || patient.phone) && (
                    <span style={{ color: "#6B7280", fontSize: "16px" }}>•</span>
                  )}
                  <MailOutlined className={styles.dashboard__tabs_patient_contact_item_icon} />
                  <Text className={styles.dashboard__tabs_patient_date}>{patient.email}</Text>
                </div>
              )}
            </Flex>
            <div className={styles.dashboard__tabs_patient_contact}>
              <div className={styles.dashboard__tabs_patient_contact_item}>
                <Text className={styles.dashboard__tabs_patient_contact_item_text}>
                  <span style={{ color: "#0D0D0D" }}>Call Summary</span> : {patient.summary || "N/A"}
                </Text>
              </div>
              <Flex gap={8}>
                <Button
                  type="default"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewTranscript(patient.id, patient.name)}
                  title="View call transcript"
                >
                  View Transcript
                </Button>
                {!showArchived && (
                  <Button
                    type="primary"
                    size="small"
                    icon={
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M0 1C0 0.734784 0.105357 0.48043 0.292893 0.292893C0.48043 0.105357 0.734784 0 1 0H11C11.2652 0 11.5196 0.105357 11.7071 0.292893C11.8946 0.48043 12 0.734784 12 1V2C12 2.26522 11.8946 2.51957 11.7071 2.70711C11.5196 2.89464 11.2652 3 11 3H1C0.734784 3 0.48043 2.89464 0.292893 2.70711C0.105357 2.51957 0 2.26522 0 2V1Z"
                          fill="#9CA3AF"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M11 4H1V10C1 10.5304 1.21071 11.0391 1.58579 11.4142C1.96086 11.7893 2.46957 12 3 12H9C9.53043 12 10.0391 11.7893 10.4142 11.4142C10.7893 11.0391 11 10.5304 11 10V4ZM6.75 5.75C6.75 5.55109 6.67098 5.36032 6.53033 5.21967C6.38968 5.07902 6.19891 5 6 5C5.80109 5 5.61032 5.07902 5.46967 5.21967C5.32902 5.36032 5.25 5.55109 5.25 5.75V8.44L4.03 7.22C3.88783 7.08752 3.69978 7.0154 3.50548 7.01882C3.31118 7.02225 3.12579 7.10097 2.98838 7.23838C2.85097 7.37579 2.77225 7.56118 2.76883 7.75548C2.7654 7.94978 2.83752 8.13783 2.97 8.28L5.47 10.78C5.61063 10.9205 5.80125 10.9993 6 10.9993C6.19875 10.9993 6.38937 10.9205 6.53 10.78L9.03 8.28C9.10369 8.21134 9.16279 8.12854 9.20378 8.03654C9.24477 7.94454 9.26682 7.84522 9.26859 7.74452C9.27037 7.64382 9.25184 7.54379 9.21412 7.4504C9.1764 7.35701 9.12026 7.27218 9.04904 7.20096C8.97782 7.12974 8.89299 7.0736 8.7996 7.03588C8.70621 6.99816 8.60618 6.97963 8.50548 6.98141C8.40478 6.98318 8.30546 7.00523 8.21346 7.04622C8.12146 7.08721 8.03866 7.14631 7.97 7.22L6.75 8.44V5.75Z"
                          fill="#9CA3AF"
                        />
                      </svg>
                    }
                    onClick={() => handleArchiveCall(patient.id)}
                    className={styles.dashboard__archive_button}
                    title="Archive this call"
                  >
                    Archive
                  </Button>
                )}
              </Flex>
            </div>
          </div>
        </List.Item>
      )}
    />
  );

  const getCallList = () => {
    let results = currentData?.calls || [];

    // Note: With server-side pagination, filtering is limited to current page data
    // For full filtering across all pages, this should be moved to server-side
    if (selectedTag) {
      // Filter by skills (only on current page)
      results = results.filter((call) =>
        call.skills?.some((skill) => skill.label.toLowerCase() === selectedTag.toLowerCase()),
      );
    }

    // Apply date filtering (only on current page)
    return filterCallsByDate(results);
  };

  const renderListContent = () => {
    const results = getCallList();
    return renderPatientList(results || []);
  };

  // Handle loading and error states
  if (currentLoading) {
    return (
      <div className={styles.dashboard__container}>
        <div className={styles.dashboard__header}>
          <div className={styles.dashboard__header_title}>Calls Dashboard</div>
          <div className={styles.dashboard__header_subtitle}>Manage patient calls and inquiries</div>
        </div>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </div>
      </div>
    );
  }

  if (currentError) {
    return (
      <div className={styles.dashboard__container}>
        <div className={styles.dashboard__header}>
          <div className={styles.dashboard__header_title}>Calls Dashboard</div>
          <div className={styles.dashboard__header_subtitle}>Manage patient calls and inquiries</div>
        </div>
        <Alert
          message="Error Loading Call Data"
          description="Unable to fetch call dashboard data. Please try again later."
          type="error"
          showIcon
          style={{
            textAlign: "left",
          }}
          action={
            <Button
              onClick={() => currentRefetch()}
              style={{ border: "none", background: "none", color: "#1890ff", cursor: "pointer" }}
            >
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  const StatisticCard = ({ title, value, icon }: { title: string; value: number; icon: React.ReactNode }) => (
    <Card className={styles.dashboard__stats_card}>
      <div className={styles.dashboard__stats_card_content}>
        <div className={styles.dashboard__stats_card_header}>
          <div className={styles.dashboard__stats_card_value}>{title.includes("Avg") ? value.toFixed(1) : value}</div>
          <div className={styles.dashboard__stats_card_icon}>{icon}</div>
          <div className={styles.dashboard__stats_card_title}>{title}</div>
        </div>
      </div>
    </Card>
  );

  if (!userInfo.is_ai_admin_worker_enabled) {
    return <CallDashboardLandingPage />;
  }

  return (
    <div className={styles.dashboard__container}>
      <TranscriptModal callId={transcriptCallId} callName={transcriptCallName} onClose={handleCloseTranscript} />

      <div className={styles.dashboard__header}>
        <div className={styles.dashboard__header_title}>Calls Dashboard</div>
        <div className={styles.dashboard__header_subtitle}>Manage patient calls and inquiries</div>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[24, 24]} className={styles.dashboard__stats}>
        <Col xs={24} sm={12} lg={6}>
          <StatisticCard
            title="Total Calls"
            value={stats.total_calls}
            icon={
              <svg
                className={styles.dashboard__stats_icon_phone}
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M0.800051 1.65005C0.800051 1.29201 0.942283 0.948629 1.19546 0.695455C1.44863 0.442281 1.79201 0.300049 2.15005 0.300049H3.18325C3.49057 0.29992 3.78874 0.404649 4.02847 0.596929C4.26821 0.789209 4.43516 1.05753 4.50175 1.35755L5.14615 4.25825C5.22063 4.59406 5.16412 4.94563 4.98816 5.24118C4.8122 5.53674 4.53006 5.75398 4.19935 5.84855L3.35965 6.08885C2.99065 6.19415 2.78095 6.58835 2.92765 6.94385C3.44972 8.20602 4.21559 9.35285 5.18142 10.3187C6.14725 11.2845 7.29407 12.0504 8.55625 12.5724C8.91175 12.7191 9.30595 12.5094 9.41125 12.1404L9.65155 11.3007C9.74612 10.97 9.96336 10.6879 10.2589 10.5119C10.5545 10.336 10.906 10.2795 11.2419 10.3539L14.1426 10.9983C14.4426 11.0649 14.7109 11.2319 14.9032 11.4716C15.0955 11.7114 15.2002 12.0095 15.2001 12.3168V13.35C15.2001 13.7081 15.0578 14.0515 14.8046 14.3046C14.5515 14.5578 14.2081 14.7 13.85 14.7H12.5001C11.466 14.7 10.4634 14.565 9.50665 14.313C7.50961 13.7828 5.68825 12.734 4.22719 11.2729C2.76613 9.81185 1.71732 7.99049 1.18705 5.99345C0.929537 5.01651 0.799458 4.01036 0.800051 3.00005V1.65005Z"
                  fill="#6366F1"
                />
              </svg>
            }
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatisticCard
            title="Urgent Calls"
            value={urgentCalls?.length || 0}
            icon={
              <svg
                className={styles.dashboard__stats_icon_clock}
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M8.00005 14.7C9.90961 14.7 11.741 13.9415 13.0912 12.5912C14.4415 11.241 15.2 9.40961 15.2 7.50005C15.2 5.59049 14.4415 3.75914 13.0912 2.40888C11.741 1.05862 9.90961 0.300049 8.00005 0.300049C6.09049 0.300049 4.25914 1.05862 2.90888 2.40888C1.55862 3.75914 0.800049 5.59049 0.800049 7.50005C0.800049 9.40961 1.55862 11.241 2.90888 12.5912C4.25914 13.9415 6.09049 14.7 8.00005 14.7ZM8.67505 3.00005C8.67505 2.82103 8.60393 2.64934 8.47735 2.52275C8.35076 2.39616 8.17907 2.32505 8.00005 2.32505C7.82103 2.32505 7.64934 2.39616 7.52275 2.52275C7.39616 2.64934 7.32505 2.82103 7.32505 3.00005V7.50005C7.32505 7.87265 7.62745 8.17505 8.00005 8.17505H11.6C11.7791 8.17505 11.9508 8.10393 12.0773 7.97735C12.2039 7.85076 12.275 7.67907 12.275 7.50005C12.275 7.32103 12.2039 7.14934 12.0773 7.02275C11.9508 6.89616 11.7791 6.82505 11.6 6.82505H8.67505V3.00005Z"
                  fill="#6366F1"
                />
              </svg>
            }
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatisticCard
            title="Calls for New Patients"
            value={stats.new_patients}
            icon={
              <svg
                className={styles.dashboard__stats_icon_user_add}
                width="18"
                height="15"
                viewBox="0 0 18 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.99977 3.00005C8.99977 3.35462 8.92993 3.70571 8.79425 4.03329C8.65856 4.36087 8.45968 4.65852 8.20896 4.90924C7.95824 5.15996 7.6606 5.35884 7.33302 5.49452C7.00544 5.63021 6.65434 5.70005 6.29977 5.70005C5.9452 5.70005 5.5941 5.63021 5.26653 5.49452C4.93895 5.35884 4.6413 5.15996 4.39058 4.90924C4.13986 4.65852 3.94098 4.36087 3.8053 4.03329C3.66961 3.70571 3.59977 3.35462 3.59977 3.00005C3.59977 2.28396 3.88423 1.59721 4.39058 1.09086C4.89693 0.584512 5.58369 0.300049 6.29977 0.300049C7.01586 0.300049 7.70261 0.584512 8.20896 1.09086C8.71531 1.59721 8.99977 2.28396 8.99977 3.00005ZM1.45327 13.2852C1.27681 13.1753 1.13508 13.0176 1.04446 12.8305C0.95384 12.6433 0.91803 12.4344 0.941171 12.2277C1.10709 10.9237 1.74285 9.7249 2.7293 8.85601C3.71576 7.98711 4.98521 7.50775 6.29977 7.50775C7.61433 7.50775 8.88378 7.98711 9.87024 8.85601C10.8567 9.7249 11.4925 10.9237 11.6584 12.2277C11.7106 12.648 11.5036 13.0557 11.1454 13.2843C9.69929 14.211 8.01729 14.7024 6.29977 14.7C4.58203 14.7029 2.8997 14.2118 1.45327 13.2852ZM14.6248 3.67505C14.6248 3.49603 14.5537 3.32434 14.4271 3.19775C14.3005 3.07116 14.1288 3.00005 13.9498 3.00005C13.7708 3.00005 13.5991 3.07116 13.4725 3.19775C13.3459 3.32434 13.2748 3.49603 13.2748 3.67505V5.47505H11.4748C11.2957 5.47505 11.1241 5.54616 10.9975 5.67275C10.8709 5.79934 10.7998 5.97103 10.7998 6.15005C10.7998 6.32907 10.8709 6.50076 10.9975 6.62735C11.1241 6.75393 11.2957 6.82505 11.4748 6.82505H13.2748V8.62505C13.2748 8.80407 13.3459 8.97576 13.4725 9.10235C13.5991 9.22893 13.7708 9.30005 13.9498 9.30005C14.1288 9.30005 14.3005 9.22893 14.4271 9.10235C14.5537 8.97576 14.6248 8.80407 14.6248 8.62505V6.82505H16.4248C16.6038 6.82505 16.7755 6.75393 16.9021 6.62735C17.0287 6.50076 17.0998 6.32907 17.0998 6.15005C17.0998 5.97103 17.0287 5.79934 16.9021 5.67275C16.7755 5.54616 16.6038 5.47505 16.4248 5.47505H14.6248V3.67505Z"
                  fill="#6366F1"
                />
              </svg>
            }
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatisticCard
            title="Calls for Existing Patients"
            value={stats.existing_patients}
            icon={
              <svg
                className={styles.dashboard__stats_icon_user_add}
                width="18"
                height="14"
                viewBox="0 0 18 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.99956 5.59995C9.71564 5.59995 10.4024 5.31549 10.9087 4.80914C11.4151 4.30279 11.6996 3.61604 11.6996 2.89995C11.6996 2.18387 11.4151 1.49711 10.9087 0.990763C10.4024 0.484415 9.71564 0.199951 8.99956 0.199951C8.28347 0.199951 7.59672 0.484415 7.09037 0.990763C6.58402 1.49711 6.29956 2.18387 6.29956 2.89995C6.29956 3.61604 6.58402 4.30279 7.09037 4.80914C7.59672 5.31549 8.28347 5.59995 8.99956 5.59995ZM5.39956 4.69995C5.39956 5.17734 5.20992 5.63518 4.87235 5.97274C4.53478 6.31031 4.07695 6.49995 3.59956 6.49995C3.12217 6.49995 2.66433 6.31031 2.32677 5.97274C1.9892 5.63518 1.79956 5.17734 1.79956 4.69995C1.79956 4.22256 1.9892 3.76472 2.32677 3.42716C2.66433 3.08959 3.12217 2.89995 3.59956 2.89995C4.07695 2.89995 4.53478 3.08959 4.87235 3.42716C5.20992 3.76472 5.39956 4.22256 5.39956 4.69995ZM1.34056 11.2934C1.18692 11.206 1.07186 11.064 1.01836 10.8956C0.859783 10.3819 0.85812 9.8327 1.01358 9.31814C1.16904 8.80359 1.47455 8.34715 1.89099 8.00727C2.30743 7.66739 2.81583 7.45955 3.35111 7.41036C3.88638 7.36117 4.42415 7.47287 4.89556 7.73115C3.92682 8.68314 3.3205 9.94322 3.18106 11.2943C3.16036 11.4941 3.16846 11.6921 3.20356 11.8829C2.5467 11.8261 1.91052 11.6248 1.34056 11.2934ZM14.7956 11.882C15.4523 11.8254 16.0885 11.6244 16.6586 11.2934C16.8119 11.2059 16.9266 11.0638 16.9799 10.8956C17.1387 10.3819 17.1405 9.83251 16.9852 9.31778C16.8298 8.80306 16.5243 8.34644 16.1079 8.00642C15.6914 7.66641 15.1828 7.45848 14.6474 7.40927C14.112 7.36007 13.5741 7.47184 13.1027 7.73025C14.0722 8.6822 14.6792 9.94267 14.819 11.2943C14.839 11.4906 14.8311 11.6887 14.7956 11.8829V11.882ZM16.1996 4.69995C16.1996 5.17734 16.0099 5.63518 15.6724 5.97274C15.3348 6.31031 14.8769 6.49995 14.3996 6.49995C13.9222 6.49995 13.4643 6.31031 13.1268 5.97274C12.7892 5.63518 12.5996 5.17734 12.5996 4.69995C12.5996 4.22256 12.7892 3.76472 13.1268 3.42716C13.4643 3.08959 13.9222 2.89995 14.3996 2.89995C14.8769 2.89995 15.3348 3.08959 15.6724 3.42716C16.0099 3.76472 16.1996 4.22256 16.1996 4.69995ZM4.77316 12.071C4.6844 11.9915 4.61558 11.8922 4.57228 11.7812C4.52897 11.6702 4.51238 11.5506 4.52386 11.432C4.63833 10.3245 5.1593 9.29877 5.98605 8.55308C6.8128 7.80738 7.88664 7.39464 9.00001 7.39464C10.1134 7.39464 11.1872 7.80738 12.014 8.55308C12.8407 9.29877 13.3617 10.3245 13.4762 11.432C13.4877 11.5506 13.4712 11.6702 13.4278 11.7812C13.3845 11.8922 13.3157 11.9915 13.2269 12.071C12.0696 13.1212 10.5623 13.7021 8.99956 13.7C7.43711 13.7019 5.9301 13.1211 4.77316 12.071Z"
                  fill="#6366F1"
                />
              </svg>
            }
          />
        </Col>
      </Row>

      {/* Segmented Tabs Section */}
      <div className={styles.dashboard__content}>
        <div className={styles.dashboard__segments}>
          <div
            style={{
              /* Text */
              color: "#6A7077",
              lineHeight: "140%",
              fontSize: "14px",
              fontWeight: "500",
              width: "80px",
            }}
          >
            <FilterFilled /> Filters:
          </div>
          <Flex wrap="wrap" gap={8}>
            {(() => {
              // Sort skills to put urgent first, then others
              const sortedSkills = skillsData?.skills
                ? [...skillsData.skills].sort((a, b) => {
                    if (a.code === "urgent") return -1;
                    if (b.code === "urgent") return 1;
                    return 0;
                  })
                : [];

              return sortedSkills.map((skill) => {
                const isSelected = selectedTag === skill.label;

                return (
                  <Tag
                    key={skill.id}
                    className={isSelected ? styles.dashboard__segments_tag_selected : styles.dashboard__segments_tag}
                    onClick={() => {
                      setSelectedTag(skill.label);
                    }}
                  >
                    {skillIcon}
                    {skill.label}
                    {isSelected && (
                      <>
                        &nbsp;
                        <CloseOutlined
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTag(undefined);
                          }}
                        />
                      </>
                    )}
                  </Tag>
                );
              });
            })()}
          </Flex>
        </div>
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={12}>
            <div className={styles.dashboard__tabs_title}>
              {showArchived ? "Archived Call History" : "Call History"}
            </div>
            {/* Refresh button */}
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => currentRefetch()}
              loading={currentLoading}
              title="Refresh call data"
              size="small"
            >
              Refresh
            </Button>
            {/* Archive button */}
            <Button
              type={showArchived ? "primary" : "default"}
              onClick={() => setShowArchived(!showArchived)}
              title={showArchived ? "View active calls" : "View archived calls"}
              size="small"
              icon={
                !showArchived ? (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M0 1C0 0.734784 0.105357 0.48043 0.292893 0.292893C0.48043 0.105357 0.734784 0 1 0H11C11.2652 0 11.5196 0.105357 11.7071 0.292893C11.8946 0.48043 12 0.734784 12 1V2C12 2.26522 11.8946 2.51957 11.7071 2.70711C11.5196 2.89464 11.2652 3 11 3H1C0.734784 3 0.48043 2.89464 0.292893 2.70711C0.105357 2.51957 0 2.26522 0 2V1Z"
                      fill="#9CA3AF"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M11 4H1V10C1 10.5304 1.21071 11.0391 1.58579 11.4142C1.96086 11.7893 2.46957 12 3 12H9C9.53043 12 10.0391 11.7893 10.4142 11.4142C10.7893 11.0391 11 10.5304 11 10V4ZM6.75 5.75C6.75 5.55109 6.67098 5.36032 6.53033 5.21967C6.38968 5.07902 6.19891 5 6 5C5.80109 5 5.61032 5.07902 5.46967 5.21967C5.32902 5.36032 5.25 5.55109 5.25 5.75V8.44L4.03 7.22C3.88783 7.08752 3.69978 7.0154 3.50548 7.01882C3.31118 7.02225 3.12579 7.10097 2.98838 7.23838C2.85097 7.37579 2.77225 7.56118 2.76883 7.75548C2.7654 7.94978 2.83752 8.13783 2.97 8.28L5.47 10.78C5.61063 10.9205 5.80125 10.9993 6 10.9993C6.19875 10.9993 6.38937 10.9205 6.53 10.78L9.03 8.28C9.10369 8.21134 9.16279 8.12854 9.20378 8.03654C9.24477 7.94454 9.26682 7.84522 9.26859 7.74452C9.27037 7.64382 9.25184 7.54379 9.21412 7.4504C9.1764 7.35701 9.12026 7.27218 9.04904 7.20096C8.97782 7.12974 8.89299 7.0736 8.7996 7.03588C8.70621 6.99816 8.60618 6.97963 8.50548 6.98141C8.40478 6.98318 8.30546 7.00523 8.21346 7.04622C8.12146 7.08721 8.03866 7.14631 7.97 7.22L6.75 8.44V5.75Z"
                      fill="#9CA3AF"
                    />
                  </svg>
                ) : (
                  <CloseOutlined />
                )
              }
              iconPosition={!showArchived ? "start" : "end"}
            >
              {"Archive"}
              {showArchived && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 1C0 0.734784 0.105357 0.48043 0.292893 0.292893C0.48043 0.105357 0.734784 0 1 0H11C11.2652 0 11.5196 0.105357 11.7071 0.292893C11.8946 0.48043 12 0.734784 12 1V2C12 2.26522 11.8946 2.51957 11.7071 2.70711C11.5196 2.89464 11.2652 3 11 3H1C0.734784 3 0.48043 2.89464 0.292893 2.70711C0.105357 2.51957 0 2.26522 0 2V1Z"
                    fill="#fff"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M11 4H1V10C1 10.5304 1.21071 11.0391 1.58579 11.4142C1.96086 11.7893 2.46957 12 3 12H9C9.53043 12 10.0391 11.7893 10.4142 11.4142C10.7893 11.0391 11 10.5304 11 10V4ZM6.75 5.75C6.75 5.55109 6.67098 5.36032 6.53033 5.21967C6.38968 5.07902 6.19891 5 6 5C5.80109 5 5.61032 5.07902 5.46967 5.21967C5.32902 5.36032 5.25 5.55109 5.25 5.75V8.44L4.03 7.22C3.88783 7.08752 3.69978 7.0154 3.50548 7.01882C3.31118 7.02225 3.12579 7.10097 2.98838 7.23838C2.85097 7.37579 2.77225 7.56118 2.76883 7.75548C2.7654 7.94978 2.83752 8.13783 2.97 8.28L5.47 10.78C5.61063 10.9205 5.80125 10.9993 6 10.9993C6.19875 10.9993 6.38937 10.9205 6.53 10.78L9.03 8.28C9.10369 8.21134 9.16279 8.12854 9.20378 8.03654C9.24477 7.94454 9.26682 7.84522 9.26859 7.74452C9.27037 7.64382 9.25184 7.54379 9.21412 7.4504C9.1764 7.35701 9.12026 7.27218 9.04904 7.20096C8.97782 7.12974 8.89299 7.0736 8.7996 7.03588C8.70621 6.99816 8.60618 6.97963 8.50548 6.98141C8.40478 6.98318 8.30546 7.00523 8.21346 7.04622C8.12146 7.08721 8.03866 7.14631 7.97 7.22L6.75 8.44V5.75Z"
                    fill="#fff"
                  />
                </svg>
              )}
            </Button>
          </Flex>

          {/* Day-based filters */}
          <div className={styles.dashboard__day_filters}>
            {dayFilters.map((filter) => (
              <Tag
                key={filter.key}
                className={
                  selectedDayFilter === filter.key
                    ? styles.dashboard__day_filter_selected
                    : styles.dashboard__day_filter
                }
                onClick={() => setSelectedDayFilter(filter.key)}
              >
                {selectedDayFilter === filter.key && <CheckOutlined style={{ marginRight: "4px" }} />}
                {filter.label}
              </Tag>
            ))}
          </div>
        </Flex>

        {renderListContent()}
      </div>
    </div>
  );
};

export default CallDashboard;
